import { API_ENDPOINTS, apiClient } from '@/shared/apiClient';
import { getAuthRuntimeSessionIdentity } from '@/features/auth/services/authRuntime';
import { authService } from '@/features/auth/services/authService';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';

type PreferenceEntry = {
  value?: unknown;
  storageKey: string;
  revision: number;
  read: number;
  dirty?: boolean;
  queue?: Promise<unknown>;
  listeners: Set<() => void>;
};

const entries = new Map<string, PreferenceEntry>();
const getOwnerKey = (): string => JSON.stringify([
  getAuthRuntimeSessionIdentity(), getAdminViewAsUserId(),
]);
const assertOwner = (owner: string) => {
  if (owner !== getOwnerKey() || !getAuthRuntimeSessionIdentity()) {
    throw new Error('Authentication context changed. Retry this action.');
  }
};
const getEntry = (projectId: string, viewKey: string, owner: string): PreferenceEntry => {
  const key = JSON.stringify([owner, projectId, viewKey]);
  let entry = entries.get(key);
  if (!entry) {
    const storageOwner = [authService.getUser()?.id ?? null, getAdminViewAsUserId()];
    const storageKey = `pm-view-preferences-v3:${JSON.stringify([storageOwner, projectId, viewKey])}`;
    entry = { revision: 0, read: 0, listeners: new Set(), storageKey };
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const saved = JSON.parse(stored);
        entry.value = saved.value;
        entry.dirty = saved.dirty === true;
      }
    } catch { /* Storage is optional; the in-memory draft remains usable. */ }
    entries.set(key, entry);
  }
  return entry;
};
const publish = (entry: PreferenceEntry, value: unknown) => {
  entry.value = value;
  try { localStorage.setItem(entry.storageKey, JSON.stringify({ value, dirty: !!entry.dirty })); } catch { /* Keep the in-memory draft when storage is unavailable. */ }
  entry.listeners.forEach(listener => listener());
};

type ProjectViewPreferenceResponse =
  | {
      data?: {
        preferences?: Record<string, unknown>;
      };
      preferences?: Record<string, unknown>;
    }
  | undefined;

const unwrapPreferences = (response: ProjectViewPreferenceResponse): Record<string, unknown> => (
  response?.preferences || response?.data?.preferences || {}
);

export const projectViewPreferencesService = {
  getOwnerKey,

  setLocal(projectId: string, viewKey: string, value: unknown, owner = getOwnerKey()) {
    assertOwner(owner);
    const entry = getEntry(projectId, viewKey, owner);
    entry.revision += 1;
    publish(entry, value);
  },

  snapshot(projectId: string, viewKey: string, owner = getOwnerKey()) {
    return getEntry(projectId, viewKey, owner).value;
  },

  subscribe(projectId: string, viewKey: string, owner: string, listener: () => void) {
    const entry = getEntry(projectId, viewKey, owner);
    entry.listeners.add(listener);
    return () => { entry.listeners.delete(listener); };
  },

  async get(projectId: string, viewKey: string, owner = getOwnerKey()): Promise<Record<string, unknown>> {
    assertOwner(owner);
    const entry = getEntry(projectId, viewKey, owner);
    if (entry.dirty) {
      // A remount/get is an explicit retry point. Never replace a recoverable
      // draft with server state before its queued write has succeeded.
      if (entry.queue) await entry.queue;
      else await projectViewPreferencesService.save(projectId, viewKey, entry.value as Record<string, unknown>, owner);
      assertOwner(owner);
      return (entry.value || {}) as Record<string, unknown>;
    }
    const revision = entry.revision;
    const read = ++entry.read;
    await entry.queue?.catch(() => undefined);
    assertOwner(owner);
    const response = await apiClient.get<ProjectViewPreferenceResponse>(
      API_ENDPOINTS.projects.viewPreferences(projectId, viewKey),
      viewKey,
    );
    assertOwner(owner);
    if (entry.revision === revision && entry.read === read) {
      publish(entry, unwrapPreferences(response));
    }
    return entry.value && typeof entry.value === 'object' && !Array.isArray(entry.value)
      ? entry.value as Record<string, unknown> : {};
  },

  async save(projectId: string, viewKey: string, preferences: Record<string, unknown>, owner = getOwnerKey()): Promise<Record<string, unknown>> {
    assertOwner(owner);
    const entry = getEntry(projectId, viewKey, owner);
    const snapshot = JSON.parse(JSON.stringify(preferences)) as Record<string, unknown>;
    entry.revision += 1;
    entry.dirty = true;
    const revision = entry.revision;
    publish(entry, snapshot);
    const pending = (entry.queue || Promise.resolve()).catch(() => undefined).then(async () => {
      assertOwner(owner);
      const response = await apiClient.put<ProjectViewPreferenceResponse>(
        API_ENDPOINTS.projects.viewPreferences(projectId, viewKey),
        { preferences: snapshot },
        viewKey,
      );
      assertOwner(owner);
      if (entry.revision === revision) {
        entry.dirty = false;
        publish(entry, entry.value);
      }
      return unwrapPreferences(response);
    });
    entry.queue = pending;
    try {
      return await pending;
    } finally {
      if (entry.queue === pending) entry.queue = undefined;
    }
  },
};
